/**
 * Single source of truth for per-page SEO metadata.
 *
 * Used in three places, so a page can never drift between them:
 *   1. server/index.js — injects these tags into the HTML it serves, so
 *      crawlers that do not run JavaScript (Facebook, LinkedIn, Slack…)
 *      still read the right title, description and preview image.
 *   2. src/App.jsx — updates the document head during client-side navigation.
 *   3. scripts/gen-sitemap.mjs — builds public/sitemap.xml.
 */

export const SITE_URL = 'https://truenorthcare.ca'
export const SITE_NAME = 'True North Care & Support'
export const DEFAULT_IMAGE = '/assets/hero-home.jpg'

export const DEFAULT_SEO = {
  title: 'Home Care Agency Calgary | True North Care & Support',
  description:
    'True North Care & Support is a Calgary home care agency providing non-medical senior care, personalized care plans, screened caregivers, and family updates.',
  image: DEFAULT_IMAGE,
}

/** Pages a search engine should index, keyed by their live route. */
export const PAGES = {
  '/': {
    title: 'Home Care Agency Calgary | True North Care & Support',
    description:
      'True North Care & Support is a Calgary home care agency providing non-medical senior care, personalized care plans, screened caregivers, and family updates.',
    image: '/assets/hero-home.jpg',
    priority: '1.0',
  },

  /* ---------------------------------------------------------- services --- */
  '/services': {
    title: 'In-Home Senior Care Services | Calgary | True North',
    description:
      'Non-medical in-home care services in Calgary: personal care, meal preparation, light housekeeping, companionship, post-hospital and specialized care.',
    image: '/assets/companionship.jpg',
    priority: '0.9',
  },
  '/services/personal-care': {
    title: 'Personal Care Calgary | Senior Daily Living Assistance',
    description:
      'Respectful personal care in Calgary for seniors who need help with bathing, dressing, grooming, mobility, and daily routines at home. Always non-medical.',
    image: '/assets/personal-care.jpg',
    priority: '0.8',
  },
  '/services/meals-housekeeping': {
    title: 'Homemaking Services Calgary | Senior Home Support',
    description:
      'Homemaking services in Calgary, including meal preparation, light housekeeping, laundry, dishes, and senior home support in a calm, cared-for home.',
    image: '/assets/meals.jpg',
    priority: '0.8',
  },
  '/services/companionship': {
    title: 'Companionship for Seniors Calgary | True North Care',
    description:
      'Companionship for seniors in Calgary through friendly visits, outings, hobbies, conversation, and social support at home from a familiar caregiver.',
    image: '/assets/companionship.jpg',
    priority: '0.8',
  },
  '/services/post-hospital-care': {
    title: 'Senior Recovery Support Calgary | Post-Hospital Care',
    description:
      'Short-term senior recovery support in Calgary after a hospital stay, with meals, routines, reminders, and non-medical help through the first weeks home.',
    image: '/assets/post-hospital.jpg',
    priority: '0.8',
  },

  '/client-directed-home-care-invoicing': {
    title: 'Client Directed Home Care Invoicing (CDHCI) Calgary | True North Care & Support',
    description:
      "True North Care & Support is an approved provider for Alberta's Client Directed Home Care Invoicing program. Learn how eligible clients can choose personalized home care and direct billing through Alberta Blue Cross.",
    image: '/assets/how-it-works.jpg',
    priority: '0.9',
  },

  /* ------------------------------------------------- specialized care --- */
  '/specialized-care': {
    title: 'Specialized Senior Care Calgary | True North Care',
    description:
      "Specialized senior care in Calgary for dementia, Alzheimer's, palliative, end-of-life, and respite needs at home, alongside your healthcare team.",
    image: '/assets/specialized.jpg',
    priority: '0.9',
  },
  '/specialized-care/dementia': {
    title: 'Dementia Home Care Calgary | Memory Care at Home',
    description:
      'Dementia home care in Calgary with calm routines, familiar caregivers, memory support, respite care, and clear family updates after every visit.',
    image: '/assets/specialized.jpg',
    priority: '0.8',
  },
  '/specialized-care/alzheimers': {
    title: "Alzheimer's Care at Home Calgary | True North Care",
    description:
      "Alzheimer's care at home in Calgary with consistent caregivers, memory-friendly routines, dignity-first support, and family updates after every visit.",
    image: '/assets/specialized.jpg',
    priority: '0.8',
  },
  '/specialized-care/palliative': {
    title: 'Palliative Care Calgary | Support at Home',
    description:
      'Non-medical palliative care support at home in Calgary, including companionship, household help, overnight senior care, and comfort for the family.',
    image: '/assets/comfort-care.jpg',
    priority: '0.8',
  },
  '/specialized-care/end-of-life': {
    title: 'End-of-Life Support Calgary | Comfort Care at Home',
    description:
      'End-of-life support in Calgary with non-medical comfort care at home, quiet companionship, practical help, and family-centered support day or night.',
    image: '/assets/comfort-care.jpg',
    priority: '0.8',
  },
  '/specialized-care/respite': {
    title: 'Respite Care Calgary | Family Caregiver Relief',
    description:
      'Respite care in Calgary for family caregiver relief, short-term senior support, companion care, and daily routines at home so you can truly rest.',
    image: '/assets/respite.jpg',
    priority: '0.8',
  },

  /* ------------------------------------------------------------ company --- */
  '/how-it-works': {
    title: 'How In-Home Care Works | True North Care Calgary',
    description:
      'How in-home senior care starts in Calgary: a free assessment, a written 5-Pillar Care Plan, a matched caregiver, and updates in the family portal.',
    image: '/assets/how-it-works.jpg',
    priority: '0.7',
  },
  '/about': {
    title: 'About True North Care & Support | Calgary Home Care',
    description:
      'A Canadian owned Calgary home care company built on listening first — screened caregivers, personalized care plans, and clear family communication.',
    image: '/assets/about.jpg',
    priority: '0.7',
  },
  '/resources': {
    title: 'Senior Care Resources & Guides | Calgary Families',
    description:
      'Free guides for Calgary families on starting the care conversation, home safety, dementia communication, costs, and avoiding caregiver burnout.',
    image: '/assets/resources.jpg',
    priority: '0.6',
  },
  '/family-room': {
    title: 'Family Room Portal | Care Updates for Families',
    description:
      "A private portal where Calgary families see daily care notes, task checklists, schedules, and billing after every visit to their loved one's home.",
    image: '/assets/family-room.jpg',
    priority: '0.6',
  },
  '/careers': {
    title: 'Caregiver Jobs Calgary | Careers at True North Care',
    description:
      'Caregiver jobs in Calgary with consistent clients, written care plans, a coordinator who answers, and respect for the relationships you build.',
    image: '/assets/careers.jpg',
    priority: '0.7',
  },
  '/contact': {
    title: 'Contact True North Care & Support | Calgary',
    description:
      'Talk to a Calgary care coordinator about in-home senior care. Call +1 (825) 779-8783 or send a message and we reply within one business day.',
    image: '/assets/office.jpg',
    priority: '0.7',
  },
  '/schedule-assessment': {
    title: 'Book a Free In-Home Care Assessment | Calgary',
    description:
      'Request a free, no-obligation in-home care assessment in Calgary. Answer a few gentle questions and a care coordinator will confirm a time that suits you.',
    image: '/assets/hero-home.jpg',
    priority: '0.9',
  },

  /* -------------------------------------------------------------- legal --- */
  '/privacy': {
    title: 'Privacy Policy | True North Care & Support',
    description:
      'How True North Care & Support collects, uses, discloses, and safeguards client personal and health information under Alberta HIA and PIPA legislation.',
    priority: '0.3',
  },
  '/terms': {
    title: 'Terms of Use | True North Care & Support',
    description:
      'The terms governing use of the True North Care & Support website, including non-medical service scope, assessments, pricing, liability, and privacy.',
    priority: '0.3',
  },
  '/accessibility': {
    title: 'Accessibility | True North Care & Support',
    description:
      'Our commitment to an accessible website for Calgary seniors and families, the standards we follow, and how to tell us about any barrier you find.',
    priority: '0.3',
  },
}

/** Signed-in and transactional routes must never reach the index. */
export const NOINDEX_PREFIXES = ['/login', '/set-password', '/portal']

export const isNoindex = (pathname) =>
  NOINDEX_PREFIXES.some((p) => pathname === p || pathname.startsWith(`${p}/`))

/** Trailing slashes and query strings must resolve to the same entry. */
const normalise = (pathname) => {
  const clean = (pathname || '/').split('?')[0].split('#')[0]
  return clean.length > 1 ? clean.replace(/\/+$/, '') : '/'
}

export function seoFor(pathname) {
  const path = normalise(pathname)
  const page = PAGES[path]

  if (page) {
    return {
      ...DEFAULT_SEO,
      ...page,
      canonical: `${SITE_URL}${path === '/' ? '/' : path}`,
      noindex: false,
    }
  }

  // Unmapped routes (portals, 404s) get the default copy and stay out of the
  // index — better a missing listing than a wrong or duplicated one.
  return { ...DEFAULT_SEO, canonical: `${SITE_URL}${path}`, noindex: true }
}

/** Indexable URLs for the sitemap, most important first. */
export const sitemapEntries = () =>
  Object.entries(PAGES).map(([path, meta]) => ({
    loc: `${SITE_URL}${path === '/' ? '/' : path}`,
    priority: meta.priority || '0.5',
  }))
